/**
 * A chip you pick, not a status you read.
 *
 * Badge is the read-only one; this is the choosable one — a signal's kind, a Sync option, a Pulse
 * rating. It reads as a small outline button until it is chosen, and then it takes --primary,
 * because "which one did I pick" has to survive a glance from across the room.
 *
 * It was a pill until the restyle; it is now an 8px shoulder like every other pressable thing, and
 * the unselected fill is the CARD rather than --popover — Tuxedo's popover is the midnight dock,
 * which would have drawn an unpicked chip as the darkest object on a white page.
 *
 * `tone` is the one deliberate exception to all of that, and it is opt-in.
 *
 * The primary fill is right where a chip row is the only choosable thing in view. It is wrong where
 * a row of them stands in a form ABOVE its own call to action: Wrapped's give form has four such
 * rows, so six chips wore the same red as "Wrap it for Gabi" and out-shouted the one button that
 * actually sends the photograph. `tone="tonal"` picks with --accent and a strong hairline instead —
 * still unmistakable from across the room, still the loudest thing in its own card, and quieter
 * than the CTA under it. The hairline is also what gives an UNPICKED chip an edge in the dark
 * looks, where --border sits a few points off --card and a row of pills read as one slab.
 *
 * A design-system component: catalogued as `Chip` (invented) in `LaunchPad/ideas/_design.md`, so
 * the spec there and the drawing here have to agree.
 */
import { Text } from '@/components/ui/text';
import { cn } from '@/lib/utils';
import { Pressable } from 'react-native';

export function Chip({
  label,
  selected,
  onPress,
  disabled,
  tone = 'primary',
  className,
}: {
  label: string;
  selected?: boolean;
  onPress?: () => void;
  disabled?: boolean;
  /**
   * How a picked chip is drawn. `primary` is the system's chip and the default; `tonal` is the
   * quieter one a form uses when its chips stand above their own submit button — see the note at
   * the top of the file. Everything else about the chip is identical.
   */
  tone?: 'primary' | 'tonal';
  className?: string;
}) {
  const tonal = tone === 'tonal';
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected: !!selected, disabled: !!disabled }}
      disabled={disabled}
      onPress={onPress}
      className={cn(
        'border-border min-h-9 flex-row items-center justify-center rounded-md border px-4 py-2',
        /* A HOVER IS NOT A CHOICE. `hover:bg-accent` was the same pale fill a picked chip's
           neighbours wear, so a pointer resting on "Not my thing" drew it as already chosen. The
           hairline goes strong over a quarter-strength wash instead: pressable, not picked. */
        selected
          ? tonal
            ? 'bg-accent border-line-strong'
            : 'bg-primary border-transparent'
          : cn(
              'bg-card hover:border-line-strong hover:bg-accent/40 active:bg-accent',
              tonal && 'border-line-strong',
            ),
        /* Off is its own surface, exactly as it is on Button — never the same fill at half strength. */
        disabled && 'bg-muted border-transparent',
        className,
      )}>
      <Text
        variant="small"
        className={cn(
          selected
            ? tonal
              ? 'text-accent-foreground font-semibold'
              : 'text-primary-foreground font-semibold'
            : 'text-foreground',
          disabled && 'text-muted-foreground',
        )}>
        {label}
      </Text>
    </Pressable>
  );
}
